import { LockOpenIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { DocumentSharingFragment, DocumentGroupSharingFragment, UserGroupFragment } from "../../../../../../__generated__/graphql";
import Avatar from "../../../../../../components/Avatar";
import { useUser } from "../../../../../../contexts/UserContext/hooks/useUser";
import { useMemo, useState } from "react";
import UserGroupModal from "../../../../../projects/pages/project/components/ProjectParticipants/components/UserGroupModal";

export default function DocumentSharedWith({
  documentSharings,
  documentGroupSharings,
}: {
  documentSharings: DocumentSharingFragment[];
  documentGroupSharings?: DocumentGroupSharingFragment[];
}) {
  const { currentOrganization } = useUser();
  const [selectedGroupForModal, setSelectedGroupForModal] =
    useState<UserGroupFragment | null>(null);
  const MAX_DISPLAYED = 2;
  
  // User can edit if they're in the same organization that created the group
  const canEditGroup = (group: UserGroupFragment) =>
    currentOrganization?.id === group.organizationId;

  const groupCount = documentGroupSharings?.length ?? 0;
  const userCount = documentSharings.length;
  const totalCount = groupCount + userCount;
  const remainingCount = totalCount - MAX_DISPLAYED;

  const isSharedWithMe = useMemo(
    () =>
      documentSharings.some(
        (documentSharing) =>
          currentOrganization?.id ===
            documentSharing.recipientUserOrganizationId &&
          documentSharing.ownerUserOrganizationId !== currentOrganization?.id
      ),
    [documentSharings, currentOrganization]
  );

  if (isSharedWithMe) {
    return (
      <div className="flex items-center gap-1">
        <LockOpenIcon className="w-4 h-4 text-secondary" />
        <div className="text-sm text-secondary">Partagé avec moi</div>
      </div>
    );
  }

  // Build display items: groups first, then users
  const displayItems: React.ReactNode[] = [];
  let itemsShown = 0;

  // Add groups first
  if (documentGroupSharings) {
    for (const groupSharing of documentGroupSharings) {
      if (itemsShown >= MAX_DISPLAYED) break;
      displayItems.push(
        <button
          key={`group-${groupSharing.id}`}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedGroupForModal(groupSharing.userGroup);
          }}
          className="flex items-center justify-center w-6 h-6 rounded-full bg-accent/20 ring-2 ring-card hover:bg-accent/30 transition-colors cursor-pointer"
          title={groupSharing.userGroup.name}
        >
          <UserGroupIcon className="w-3 h-3 text-accent" />
        </button>
      );
      itemsShown++;
    }
  }

  // Add users
  for (const documentSharing of documentSharings) {
    if (itemsShown >= MAX_DISPLAYED) break;
    displayItems.push(
      <Avatar
        key={`user-${documentSharing.id}`}
        size="small"
        userOrganization={documentSharing.recipientPublicUserOrganization}
      />
    );
    itemsShown++;
  }

  return (
    <>
      <UserGroupModal
        isOpen={!!selectedGroupForModal}
        onClose={() => setSelectedGroupForModal(null)}
        userGroup={selectedGroupForModal}
        readOnly={selectedGroupForModal ? !canEditGroup(selectedGroupForModal) : true}
        projectId={selectedGroupForModal?.projectId ?? undefined}
        eventId={selectedGroupForModal?.eventId ?? undefined}
      />

      <div className="flex items-center gap-1">
        <LockOpenIcon className="w-4 h-4 text-secondary" />
        <div className="text-sm text-secondary">Partagé avec</div>
        <div className="flex -space-x-2 items-center">
          {displayItems}
          {remainingCount > 0 && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full text-sm bg-accent text-dark ring-2 ring-card">
              <div className="text-xs font-bold">+{remainingCount}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
